import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { MeResponse } from '../api/types'

const ONBOARDING_KEY = 'reisepilot.onboarding.done'

export const useSessionStore = defineStore('session', () => {
  const me = ref<MeResponse | null>(null)
  const loading = ref(false)
  const checked = ref(false)
  const error = ref<string | null>(null)
  const onboardingDone = ref(localStorage.getItem(ONBOARDING_KEY) === '1')

  const isAuthenticated = computed(() => me.value !== null)
  const timezone = computed(() => me.value?.user.timezone ?? 'Europe/Berlin')
  const vapidPublicKey = computed(() => me.value?.vapidPublicKey ?? null)

  async function fetchMe(): Promise<MeResponse | null> {
    loading.value = true
    error.value = null
    try {
      me.value = await api.me()
    } catch {
      me.value = null
    } finally {
      loading.value = false
      checked.value = true
    }
    return me.value
  }

  async function login(password: string): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      await api.login(password)
      await fetchMe()
      return me.value !== null
    } catch (caught) {
      error.value = errorMessage(caught)
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.logout()
    } finally {
      me.value = null
      checked.value = true
    }
  }

  function completeOnboarding(): void {
    onboardingDone.value = true
    localStorage.setItem(ONBOARDING_KEY, '1')
  }

  function clear(): void {
    me.value = null
    checked.value = true
  }

  return {
    me,
    loading,
    checked,
    error,
    onboardingDone,
    isAuthenticated,
    timezone,
    vapidPublicKey,
    fetchMe,
    login,
    logout,
    completeOnboarding,
    clear,
  }
})
